import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { ConfirmDialog } from '@/components/ui/dialog';
import { useAcceptImport, useDiscardImport, useImport, useSetMatch } from '@/hooks/useImports';
import { useClients, useCreateClient } from '@/hooks/useReference';
import { useBillingNouns } from '@/hooks/useSession';
import { hoursShort, money } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ImportBatchDetail, ImportGroup, NormalizedInvoice } from '@/types';

const SOURCE_LABELS: Record<string, string> = {
  google_sheets: 'Google Sheets',
  excel: 'Excel',
  csv: 'CSV',
  harvest: 'Harvest',
  toggl: 'Toggl Track',
  clockify: 'Clockify',
  qbtime: 'QuickBooks Time',
  freshbooks: 'FreshBooks',
  clio: 'Clio',
  paper: 'Paper scan',
  scan: 'Paper scan'
};

/** 'google_sheets' -> 'Google Sheets'; an unknown id is shown as-is. */
export function sourceLabel(source: string | null | undefined) {
  if (!source) return 'import';
  return SOURCE_LABELS[source] ?? source;
}

export function plural(n: number, one: string, many = `${one}s`) {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

const REVIEW_PARAM = 'review';

/**
 * The review sheet for a pending import (ON15/ON16): resolve the clients that
 * matched nothing, accept or skip each group, see what accepting changes, then
 * accept or discard. Opens from `?review=<batchId>` unless a batchId is passed.
 */
export function ReviewImport({ batchId }: { batchId?: string }) {
  const [params, setParams] = useSearchParams();
  const id = batchId ?? params.get(REVIEW_PARAM) ?? undefined;
  // A prop-driven sheet has no param to drop, so closing remembers the id.
  // A param-driven one is open exactly while the param is there, so Review
  // can open the same batch again after it was closed.
  const [closedId, setClosedId] = useState<string | null>(null);
  const open = batchId ? closedId !== batchId : Boolean(id);

  const detail = useImport(open ? id : undefined);
  const accept = useAcceptImport();
  const discard = useDiscardImport();
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const close = () => {
    if (id) setClosedId(id);
    if (params.has(REVIEW_PARAM)) {
      setParams(
        (prev) => {
          prev.delete(REVIEW_PARAM);
          return prev;
        },
        { replace: true }
      );
    }
  };

  if (!id) return null;

  const batch = detail.data;
  const groups = batch?.groups ?? [];
  const unmatched = groups.filter((g) => g.unmatched);
  const matched = groups.filter((g) => !g.unmatched);
  const busy = accept.isPending || discard.isPending;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && !busy && close()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-zinc-950/30 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex w-full max-w-[640px] flex-col overflow-y-auto scroll-thin',
            'border-l border-[var(--line)] bg-[var(--surface)] shadow-xl focus:outline-none',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right'
          )}
        >
          <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-6">
            <div className="min-w-0 space-y-1">
              <DialogPrimitive.Title className="text-lg font-semibold tracking-tight">
                Review import from {sourceLabel(batch?.source)}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="text-sm text-muted">
                {batch ? <HeaderCounts batch={batch} /> : 'Loading…'}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="iconSm" aria-label="Close">
                <X />
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 space-y-8 px-6 pb-6">
            {detail.isError ? (
              <p className="text-sm text-red-600 dark:text-red-400">Could not load this import.</p>
            ) : null}

            {batch && batch.status !== 'pending' ? (
              <p className="rounded-xl border border-[var(--line)] bg-[var(--surface-2)] px-4 py-3 text-sm text-muted">
                This import is {batch.status}. There is nothing left to review.
              </p>
            ) : null}

            {batch && unmatched.length ? (
              <section className="space-y-3">
                <SectionTitle>Needs a match first</SectionTitle>
                <ul className="divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)]">
                  {unmatched.map((group) => (
                    <UnmatchedRow key={group.groupKey} batchId={batch.id} group={group} groups={groups} />
                  ))}
                </ul>
              </section>
            ) : null}

            {batch && matched.length ? (
              <section className="space-y-3">
                <SectionTitle>Matched</SectionTitle>
                <ul className="divide-y divide-[var(--line)] rounded-2xl border border-[var(--line)]">
                  {matched.map((group) => (
                    <MatchedRow key={group.groupKey} batchId={batch.id} group={group} />
                  ))}
                </ul>
              </section>
            ) : null}

            {batch ? <ChangeSummary batch={batch} /> : null}
          </div>

          {batch && batch.status === 'pending' ? (
            <div className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-[var(--line)] bg-[var(--surface)] px-6 py-4">
              <Button variant="dangerGhost" onClick={() => setConfirmDiscard(true)} disabled={busy}>
                Discard import
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="primary"
                  loading={accept.isPending}
                  disabled={busy || unmatched.length > 0 || groups.length === 0}
                  title={unmatched.length ? 'Match or skip every client first.' : undefined}
                  onClick={() => accept.mutate({ id: batch.id, mode: 'all' }, { onSuccess: close })}
                >
                  Accept all
                </Button>
              </div>
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>

      {batch ? (
        <ConfirmDialog
          open={confirmDiscard}
          onOpenChange={setConfirmDiscard}
          title="Discard this import?"
          description={`${plural(batch.summary?.counts?.entry ?? 0, 'entry', 'entries')} from ${sourceLabel(batch.source)} will be thrown away. Nothing was added to your books.`}
          confirmLabel="Discard"
          destructive
          onConfirm={() => discard.mutate(batch.id, { onSuccess: close })}
        />
      ) : null}
    </DialogPrimitive.Root>
  );
}

function SectionTitle({ children }: { children: string }) {
  return <h3 className="text-sm font-semibold tracking-tight">{children}</h3>;
}

function HeaderCounts({ batch }: { batch: ImportBatchDetail }) {
  const nouns = useBillingNouns();
  const c = batch.summary?.counts ?? {};
  const parts = [
    plural(c.entry ?? 0, 'entry', 'entries'),
    plural(c.client ?? 0, nouns.clientLower),
    plural(c.activity ?? 0, 'activity', 'activities')
  ];
  if (c.invoice) parts.push(plural(c.invoice, 'invoice'));
  return (
    <>
      {parts.join(' · ')}. Nothing counts until you accept.
    </>
  );
}

function groupCounts(group: ImportGroup) {
  const entries = group.counts?.entry ?? 0;
  const fees = group.counts?.fee ?? 0;
  const invoices = group.counts?.invoice ?? 0;
  const parts: string[] = [];
  if (entries || (!fees && !invoices)) parts.push(`${plural(entries, 'entry', 'entries')} · ${hoursShort(group.hours)} h`);
  if (fees) parts.push(plural(fees, 'fee'));
  if (invoices) parts.push(plural(invoices, 'invoice'));
  return parts.join(' · ');
}

function useClientOptions() {
  const clients = useClients();
  const options = useMemo(
    () => (clients.data ?? []).map((c) => ({ value: c.id, label: c.name, billableId: c.billable?.id })),
    [clients.data]
  );
  const nameOf = (id: string | undefined) => clients.data?.find((c) => c.id === id)?.name;
  return { options, nameOf, loading: clients.isLoading };
}

/** What an unmatched group holds, as its row's title: "Invoice 2026-081", "1 fee", "3 entries · 2.5 h". */
function unmatchedTitle(group: ImportGroup) {
  if (!group.payer) return group.name;
  if (group.counts?.invoice && !group.counts?.entry && !group.counts?.fee) return group.name;
  const counts = groupCounts(group);
  return counts.charAt(0).toUpperCase() + counts.slice(1);
}

function UnmatchedRow({
  batchId,
  group,
  groups
}: {
  batchId: string;
  group: ImportGroup;
  groups: ImportGroup[];
}) {
  const nouns = useBillingNouns();
  const setMatch = useSetMatch(batchId);
  const createClient = useCreateClient();
  const { options: existing, loading } = useClientOptions();
  const target = { groupKey: group.groupKey };
  const payer = group.payer;
  // Clients this import creates can be picked too; they do not exist yet, so
  // they are keyed by their group and resolved when the batch is accepted.
  const inBatch = groups
    .filter((g) => !g.unmatched && g.action === 'create' && g.groupKey !== group.groupKey && (g.counts?.client ?? 0) > 0)
    .map((g) => ({ value: `key:${g.groupKey}`, label: g.name, hint: 'in this import' }));
  const all = [...inBatch, ...existing];
  // Logged on a payer: offer that payer's clients, which is where it belongs.
  const ofPayer = payer
    ? all.filter((o) =>
        o.value.startsWith('key:')
          ? payer.clientKeys.includes(o.value.slice(4))
          : Boolean(payer.existingId) && 'billableId' in o && o.billableId === payer.existingId
      )
    : [];
  const options = ofPayer.length ? ofPayer : all;
  const amount = group.amount ? money(group.amount) : null;

  return (
    <li className="space-y-3 px-4 py-3">
      <div className="space-y-0.5">
        <div className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate font-medium">{unmatchedTitle(group)}</span>
          <span className="shrink-0 text-sm text-muted tabular-nums">
            {payer ? amount : [groupCounts(group), amount].filter(Boolean).join(' · ')}
          </span>
        </div>
        {payer ? (
          <p className="text-sm text-muted">
            Billed to {payer.name}. Which {nouns.clientLower} is it for?
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Combobox
            options={options}
            value={null}
            loading={loading}
            disabled={setMatch.isPending}
            placeholder={`Pick a ${nouns.clientLower}`}
            createLabel={`New ${nouns.clientLower}`}
            onChange={(value) =>
              setMatch.mutate({
                target,
                match: value.startsWith('key:')
                  ? { action: 'use', targetKey: value.slice(4) }
                  : { action: 'use', targetId: value }
              })
            }
            onCreate={async (name) => {
              const client = await createClient.mutateAsync({ name });
              await setMatch.mutateAsync({ target, match: { action: 'use', targetId: client.id } });
            }}
          />
        </div>
        <span className="text-sm text-subtle">or</span>
        <Button
          variant="secondary"
          className="max-w-[45%]"
          title={payer ? `Make ${payer.name} a ${nouns.clientLower} of its own` : undefined}
          loading={setMatch.isPending && setMatch.variables?.match.action === 'create'}
          onClick={() => setMatch.mutate({ target, match: { action: 'create' } })}
        >
          <span className="truncate">{payer ? `Keep on ${payer.name}` : 'Create it'}</span>
        </Button>
      </div>
    </li>
  );
}

function MatchedRow({ batchId, group }: { batchId: string; group: ImportGroup }) {
  const nouns = useBillingNouns();
  const setMatch = useSetMatch(batchId);
  const { nameOf } = useClientOptions();
  const target = { groupKey: group.groupKey };
  const skipped = group.action === 'skip';
  const existingName = group.existingClientId ? nameOf(group.existingClientId) : undefined;
  const suggestion =
    group.mergeSuggestion && group.mergeSuggestion !== group.existingClientId
      ? group.mergeSuggestion
      : undefined;

  const where = skipped
    ? 'skipped'
    : group.existingClientId
      ? `→ ${existingName ?? `existing ${nouns.clientLower}`}, existing`
      : `→ new ${nouns.clientLower}`;

  return (
    <li className={cn('space-y-2 px-4 py-3', skipped && 'opacity-60')}>
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="min-w-0 truncate font-medium">{group.name}</span>
            <span className="shrink-0 truncate text-sm text-muted">{where}</span>
          </div>
          <p className="text-sm text-muted tabular-nums">{groupCounts(group)}</p>
        </div>
        {skipped ? (
          <Button
            variant="secondary"
            size="sm"
            loading={setMatch.isPending}
            onClick={() =>
              setMatch.mutate({
                target,
                match: group.existingClientId
                  ? { action: 'use', targetId: group.existingClientId }
                  : { action: 'create' }
              })
            }
          >
            Accept
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            loading={setMatch.isPending}
            onClick={() => setMatch.mutate({ target, match: { action: 'skip' } })}
          >
            Skip
          </Button>
        )}
      </div>
      {suggestion && !skipped ? (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-[var(--accent-soft)]/50 px-3 py-2 text-sm">
          <span className="min-w-0 truncate">
            Looks like {nameOf(suggestion) ?? `an existing ${nouns.clientLower}`}. Merge into it?
          </span>
          <Button
            variant="link"
            size="sm"
            loading={setMatch.isPending}
            onClick={() => setMatch.mutate({ target, match: { action: 'use', targetId: suggestion } })}
          >
            Merge
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** "What changes when you accept", from the groups and the batch summary. */
function ChangeSummary({ batch }: { batch: ImportBatchDetail }) {
  const nouns = useBillingNouns();
  const groups = batch.groups ?? [];
  const counts = batch.summary?.counts ?? {};

  const accepted = groups.filter((g) => !g.unmatched && g.action !== 'skip');
  const merged = accepted.filter((g) => g.action === 'use' && g.existingClientId).length;
  const clients = accepted.filter((g) => (g.counts?.client ?? 0) > 0).length;
  const skippedEntries =
    groups
      .filter((g) => g.unmatched || g.action === 'skip')
      .reduce((n, g) => n + (g.counts?.entry ?? 0), 0) + (batch.summary?.duplicates ?? 0);
  const entries = Math.max(0, (counts.entry ?? 0) - skippedEntries);

  const invoices = (batch.items?.invoice ?? []).filter((i) => i.match?.action !== 'skip');
  const paid = invoices.filter((i) => (i.payload as unknown as NormalizedInvoice).status === 'paid').length;
  const sent = invoices.length - paid;

  const lines: string[] = [];
  if (clients) {
    lines.push(
      `${plural(clients, nouns.clientLower)}${merged ? ` (${merged} merged into existing)` : ''}`
    );
  }
  if (counts.billable) lines.push(plural(counts.billable, nouns.payer.toLowerCase(), nouns.payers.toLowerCase()));
  if (counts.activity) lines.push(plural(counts.activity, 'activity', 'activities'));
  lines.push(
    `${plural(entries, 'entry', 'entries')}${skippedEntries ? ` (${skippedEntries} skipped)` : ''}`
  );
  if (invoices.length) {
    const how = [paid ? `${paid} as Paid` : null, sent ? `${sent} as Sent` : null]
      .filter(Boolean)
      .join(', ');
    lines.push(`${plural(invoices.length, 'old invoice')} ${how}`);
  }
  if (counts.fee) lines.push(plural(counts.fee, 'fee'));

  return (
    <section className="rounded-2xl border border-[var(--line)] bg-[var(--surface-2)] px-4 py-3">
      <h3 className="text-sm font-semibold tracking-tight">What changes when you accept</h3>
      <ul className="mt-2 space-y-1 text-sm text-muted">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </section>
  );
}
